import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { hasIndividualAccess } from '../../../utils/userRoles';
import { useRoleContext } from '../../../context/RoleContext';
import { CONTEXT_TYPES, ROLE_CONTEXT_LABELS } from '../../../constants/roleContext';
import { fetchTournamentJoinLink, fetchTournaments } from '../../../services/tournamentService';
import PageShell from '../../../components/layout/PageShell';
import { getTournamentOrganizerLabel } from '../../../utils/tournamentPresentation';

const normalizeId = (value) => {
  if (!value) return '';

  return value.toString ? value.toString() : `${value}`;
};

const normalizeText = (value) => (value || '').toString().trim().toLowerCase();

const formatDate = (value) => {
  if (!value) return 'Date TBD';

  try {
    return new Date(value).toLocaleDateString();
  } catch (error) {
    return 'Date TBD';
  }
};

const sortTournaments = (list = []) =>
  [...list].sort((a, b) => {
    if (a?.isActive !== b?.isActive) {
      return a?.isActive === false ? 1 : -1;
    }
    return (a?.name || '').localeCompare(b?.name || '');
  });

const getTournamentDateRange = (tournament) => {
  const dates = (tournament?.matchSchedule?.matches || [])
    .map((match) => Date.parse(match?.date))
    .filter((timestamp) => Number.isFinite(timestamp));

  if (!dates.length) return null;

  return {
    start: new Date(Math.min(...dates)),
    end: new Date(Math.max(...dates)),
  };
};

const hasScoringStarted = (match) => {
  if (!match?.score) return false;
  if (match.score.matchComplete) return true;
  if (match.score.status && match.score.status !== 'not_started') return true;
  if (Number(match.score.balls) > 0) return true;
  if (Array.isArray(match.score.events) && match.score.events.length > 0) return true;
  return Number(match.score.runs) > 0 || Number(match.score.wickets) > 0;
};

const getTournamentPhase = (tournament) => {
  const matches = tournament?.matchSchedule?.matches || [];
  if (matches.some((match) => hasScoringStarted(match))) {
    return 'ongoing';
  }

  const range = getTournamentDateRange(tournament);
  if (!range) return 'upcoming';

  const today = new Date();
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const todayEnd = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999);

  if (range.end < todayStart) return 'past';
  if (range.start > todayEnd) return 'upcoming';
  return 'ongoing';
};

const getTournamentId = (tournament) =>
  tournament?._id
  || tournament?.id
  || tournament?.tournamentId
  || tournament?.tournament?._id
  || tournament?.tournament?.id
  || '';

const normalizeTournamentList = (tournaments = []) =>
  tournaments.map((item) => ({
    ...item,
    _id: item?._id || item?.id || item?.tournamentId || item?.tournament?._id || '',
  }));

// ✅ Custom breadcrumbs example:
// const crumbs = [
//   { label: 'Home', to: '/' },
//   { label: 'Community', to: `/community/${communityId}` },
//   { label: 'Match Manager', to: `/community/${communityId}/match-manager/${managerId}` },
//   { label: 'Manual Match Format' },
// ];
// <PageShell breadcrumbs={crumbs}>...</PageShell>
// <PageShell breadcrumbOverrides={{ tournamentId: tournament?.name || 'Tournament' }}>
const IndividualTournamentsPage = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const { availableContexts, switchRole, getLandingPath } = useRoleContext();
  const [allTournaments, setAllTournaments] = useState([]);
  const [myTournaments, setMyTournaments] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [myTab, setMyTab] = useState('ongoing');
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [joinLinkByTournamentId, setJoinLinkByTournamentId] = useState({});
  const toast = useMemo(() => ({
    error: (message) => setLoadError(message),
  }), []);

  const hasIndividualRole = hasIndividualAccess(user)
    || availableContexts.some((context) => context.type === CONTEXT_TYPES.INDIVIDUAL);

  useEffect(() => {
    let isMounted = true;

    const loadTournaments = async () => {
      if (!token || !hasIndividualRole) return;

      setIsLoading(true);
      setLoadError('');

      const [myResult, allResult] = await Promise.allSettled([
        fetchTournaments(token, null, { view: 'active' }),
        fetchTournaments(token, null, { view: 'all' }),
      ]);

      if (!isMounted) return;

      if (myResult.status === 'fulfilled') {
        setMyTournaments(normalizeTournamentList(myResult.value.tournaments || []));
      }

      if (allResult.status === 'fulfilled') {
        setAllTournaments(normalizeTournamentList(allResult.value.tournaments || []));
      }

      if (myResult.status === 'rejected' || allResult.status === 'rejected') {
        const errorMessage =
          myResult.status === 'rejected'
            ? myResult.reason?.message || 'Unable to load tournaments.'
            : allResult.reason?.message || 'Unable to load tournaments.';
        setLoadError(errorMessage);
      }

      setIsLoading(false);
    };

    loadTournaments();

    return () => {
      isMounted = false;
    };
  }, [hasIndividualRole, token]);

  const sortedAllTournaments = useMemo(() => sortTournaments(allTournaments || []), [allTournaments]);
  const sortedMyTournaments = useMemo(() => sortTournaments(myTournaments || []), [myTournaments]);

  const myTournamentsByPhase = useMemo(() => {
    const groups = {
      past: [],
      ongoing: [],
      upcoming: [],
    };

    sortedMyTournaments.forEach((tournament) => {
      const phase = getTournamentPhase(tournament);
      groups[phase].push(tournament);
    });

    return groups;
  }, [sortedMyTournaments]);

  const activeMyTournaments = myTournamentsByPhase[myTab] || [];

  const adminRoles = useMemo(
    () => availableContexts.filter((role) => [
      CONTEXT_TYPES.ORGANIZATION_ADMIN,
      CONTEXT_TYPES.CLUB_ADMIN,
      CONTEXT_TYPES.COMMUNITY_ADMIN,
      CONTEXT_TYPES.TOURNAMENT_ADMIN,
      CONTEXT_TYPES.DEPARTMENT_ADMIN,
    ].includes(role.type)),
    [availableContexts]
  );

  const handleAdminRoleSelect = (role) => {
    switchRole(role);
    navigate(getLandingPath(role), { replace: true });
  };

  const renderTournamentCard = (tournament, { showMatches = true } = {}) => {
    const resolvedTournamentId = normalizeId(getTournamentId(tournament));
    const tournamentId = resolvedTournamentId;
    const organizationName = tournament?.organization?.name || 'Individual Tournament';
    const statusLabel = tournament?.isActive !== false ? 'Active' : 'Archived';
    const leaderboardPath = tournamentId ? `/public/tournaments/${tournamentId}/leaderboard` : '';
    const matchesPath = tournamentId ? `/dashboard/individual/tournaments/${tournamentId}/matches` : '';
    const isClickable = showMatches && matchesPath;
    const registrationCount = Number(tournament?.registrationCount || tournament?.players?.length || 0);
    const maxPlayersAllowed = Number(tournament?.maxPlayersAllowed || 0);
    const isRegistrationClosed = Boolean(tournament?.registrationClosed);
    const isSelectedForJoinLink = Boolean(joinLinkByTournamentId[tournamentId]);
    const ownerType = normalizeText(tournament?.ownerType);
    const ownerUserId = normalizeId(tournament?.ownerUser?._id || tournament?.ownerUser);
    const createdById = normalizeId(tournament?.createdBy?._id || tournament?.createdBy);
    const currentUserId = normalizeId(user?._id || user?.id);
    const canManage = ownerType === 'individual' && (ownerUserId === currentUserId || createdById === currentUserId);
    const managePath = resolvedTournamentId ? `/individual-tournament/${resolvedTournamentId}/admin` : '';
    const editPath = resolvedTournamentId ? `/dashboard/individual/tournaments/${resolvedTournamentId}/edit` : '';
    const bannerSrc = tournament?.tournamentBanner || tournament?.bannerImage || '';
    const logoSrc = tournament?.tournamentLogo || tournament?.logo || '';
    const organizerLabel = getTournamentOrganizerLabel(tournament);

    return (
      <div
        key={tournamentId}
        className={`card border-0 shadow-sm ${isClickable ? 'cursor-pointer' : ''}`}
        role={isClickable ? 'button' : undefined}
        tabIndex={isClickable ? 0 : undefined}
        onClick={() => {
          if (isClickable) {
            navigate(matchesPath);
          }
        }}
        onKeyDown={(event) => {
          if (!isClickable) return;
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            navigate(matchesPath);
          }
        }}
      >
        <div className="card-body d-flex flex-column flex-lg-row justify-content-lg-between gap-3">
          <div>
            {bannerSrc ? <img src={bannerSrc} alt={`${tournament?.name || 'Tournament'} banner`} className="img-fluid rounded mb-2" style={{ maxHeight: 120, width: '100%', objectFit: 'cover' }} /> : null}
            {logoSrc ? <img src={logoSrc} alt={`${tournament?.name || 'Tournament'} logo`} className="mb-2" style={{ height: 56, width: 56, borderRadius: '50%', objectFit: 'cover' }} /> : null}
            <p className="text-muted small mb-1">{organizationName}</p>
            <h2 className="h6 mb-1">{tournament?.name || 'Untitled tournament'}</h2>
            {organizerLabel ? <p className="text-muted small mb-1 text-truncate" title={organizerLabel}>{organizerLabel}</p> : null}
            <p className="small mb-1"><span className="badge text-bg-primary">Individual Tournament</span></p>
            <p className="text-muted small mb-0">
              Created {formatDate(tournament?.createdAt)} · Updated {formatDate(tournament?.updatedAt)}
            </p>
            <p className="text-muted small mb-0">
              Registrations: {registrationCount}{maxPlayersAllowed ? ` / ${maxPlayersAllowed}` : ''}
              {isRegistrationClosed ? ' · Registrations are closed' : ''}
            </p>
            {isSelectedForJoinLink && (
              <p className="small mb-0 mt-1">
                Join link: <span className="text-primary">{joinLinkByTournamentId[tournamentId]}</span>
              </p>
            )}
          </div>
          <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center gap-2">
            <span
              className={`badge ${
                tournament?.isActive !== false
                  ? 'bg-success-subtle text-success-emphasis'
                  : 'bg-secondary-subtle text-secondary-emphasis'
              }`}
            >
              {statusLabel}
            </span>
            {showMatches && matchesPath && (
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={(event) => {
                  event.stopPropagation();
                  navigate(matchesPath);
                }}
              >
                View matches
              </button>
            )}
            {canManage && managePath ? (
              <button type="button" className="btn btn-sm btn-primary" onClick={(event) => {
                event.stopPropagation();
                const targetUrl = `/individual-tournament/${resolvedTournamentId}/admin`;
                if (!resolvedTournamentId) {
                  toast.error('Tournament ID missing. Please refresh and try again.');
                  return;
                }
                navigate(targetUrl);
              }}
              >
                Manage Tournament
              </button>
            ) : null}
            {canManage && editPath ? (
              <button type="button" className="btn btn-sm btn-outline-primary" onClick={(event) => {
                event.stopPropagation();
                if (!resolvedTournamentId) {
                  toast.error('Tournament ID missing. Please refresh and try again.');
                  return;
                }
                const targetUrl = `/dashboard/individual/tournaments/${resolvedTournamentId}/edit`;
                navigate(targetUrl);
              }}
              >
                Edit Tournament
              </button>
            ) : null}
            {leaderboardPath && (
              <Link to={leaderboardPath} className="btn btn-sm btn-outline-primary">
                View leaderboard
              </Link>
            )}
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={async (event) => {
                event.stopPropagation();
                if (!token || !tournamentId) return;
                try {
                  const response = await fetchTournamentJoinLink(tournamentId, token);
                  const joinPath = response?.joinUrl || response?.link || '';
                  const absolute = /^https?:\/\//i.test(joinPath)
                    ? joinPath
                    : `${window.location.origin}${joinPath.startsWith('/') ? '' : '/'}${joinPath}`;
                  setJoinLinkByTournamentId((prev) => ({ ...prev, [tournamentId]: absolute }));
                  await navigator.clipboard.writeText(absolute);
                } catch (_error) {
                  setLoadError('Unable to generate join link.');
                }
              }}
            >
              Copy join link
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  if (!hasIndividualRole) {
    return <Navigate to="/dashboard/individual/home" replace />;
  }

  return (
    <PageShell containerClassName="p-0">
    <div className="py-4 d-grid gap-3">
      <div className="d-flex flex-column flex-lg-row justify-content-lg-between align-items-lg-center gap-2">
        <div>
          <p className="eyebrow text-primary mb-1">Individual account</p>
          <h1 className="h4 mb-2">Tournaments</h1>
          <p className="text-muted mb-0">
            Browse all tournaments or focus on the ones you are participating in or assigned to officiate.
          </p>
        </div>
        <Link className="btn btn-primary" to="/dashboard/individual/tournaments/create">Add A Tournament</Link>
      </div>

      <div className="d-flex flex-wrap gap-2 align-items-center" role="tablist" aria-label="Role tabs">
        <button type="button" className="btn btn-primary" aria-current="page">Individual</button>
        {adminRoles.map((role) => (
          <button
            key={`${role.type}:${role.entityId || 'none'}`}
            type="button"
            className="btn btn-outline-primary"
            onClick={() => handleAdminRoleSelect(role)}
          >
            {ROLE_CONTEXT_LABELS[role.type] || role.label}
            {role.entityName ? ` · ${role.entityName}` : ''}
          </button>
        ))}
      </div>

      <div className="card border-0 shadow-sm">
        <div className="card-body pb-0">
          <ul className="nav nav-tabs" role="tablist">
            <li className="nav-item" role="presentation">
              <button
                type="button"
                className={`nav-link ${activeTab === 'all' ? 'active' : ''}`}
                role="tab"
                aria-selected={activeTab === 'all'}
                onClick={() => setActiveTab('all')}
              >
                All tournaments
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                type="button"
                className={`nav-link ${activeTab === 'mine' ? 'active' : ''}`}
                role="tab"
                aria-selected={activeTab === 'mine'}
                onClick={() => setActiveTab('mine')}
              >
                My tournaments
              </button>
            </li>
          </ul>
        </div>
        {activeTab === 'mine' && (
          <div className="card-body pt-3">
            <ul className="nav nav-pills gap-2" role="tablist">
              {[
                { key: 'ongoing', label: 'Ongoing tournaments' },
                { key: 'upcoming', label: 'Upcoming tournaments' },
                { key: 'past', label: 'Past tournaments' },
              ].map((tab) => (
                <li key={tab.key} className="nav-item" role="presentation">
                  <button
                    type="button"
                    className={`nav-link ${myTab === tab.key ? 'active' : ''}`}
                    role="tab"
                    aria-selected={myTab === tab.key}
                    onClick={() => setMyTab(tab.key)}
                  >
                    {tab.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {loadError && <div className="alert alert-danger mb-0">{loadError}</div>}
      {isLoading && <div className="alert alert-secondary mb-0">Loading tournaments...</div>}

      {!isLoading && activeTab === 'all' && sortedAllTournaments.length === 0 && (
        <div className="alert alert-info mb-0 d-flex justify-content-between align-items-center">
          <span>No tournaments are available yet.</span>
          <Link className="btn btn-sm btn-primary" to="/dashboard/individual/tournaments/create">Create Individual Tournament</Link>
        </div>
      )}

      {!isLoading && activeTab === 'mine' && activeMyTournaments.length === 0 && (
        <div className="alert alert-info mb-0">No tournaments match this view yet.</div>
      )}

      {!isLoading &&
        activeTab === 'all' &&
        sortedAllTournaments.map((tournament) => renderTournamentCard(tournament, { showMatches: false }))}

      {!isLoading &&
        activeTab === 'mine' &&
        activeMyTournaments.map((tournament) => renderTournamentCard(tournament, { showMatches: true }))}
    </div>
  
    </PageShell>
  );
};

export default IndividualTournamentsPage;
